/**
 * Custom Reports Library
 *
 * File-structure view of all saved custom reports.
 * Create, browse, open, rename, delete custom reports.
 *
 * Route: /prioritization/reports/custom
 */

import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Plus,
  Folder,
  Trash2,
  Pencil,
  Search,
  Check,
  X,
  BookOpen,
  Calendar,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { migrateLocalCustomReports } from '../../../lib/migrateLocalCustomReports';
import { JustMeTag } from '../../../components/prioritization/reports/ReportAudience';

// ── Inline rename ────────────────────────────────────────────────────────────

function InlineRename({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(value);
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.preventDefault()}>
      <input
        autoFocus
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="flex-1 px-2 py-1 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 min-w-0"
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(val.trim());
          if (e.key === 'Escape') onCancel();
          e.stopPropagation();
        }}
      />
      <button type="button" onClick={() => onSave(val.trim())} className="p-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]">
        <Check className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onCancel} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function CustomReportsLibraryPage() {
  const navigate = useNavigate();
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? 'default';
  const addNotification = useUIStore((s) => s.addNotification);

  const allReports = useSavedReportsStore((s) => s.reports);
  const fetchFromSupabase = useSavedReportsStore((s) => s.fetchFromSupabase);
  const deleteReport = useSavedReportsStore((s) => s.deleteReport);
  const updateReport = useSavedReportsStore((s) => s.updateReport);

  const [query, setQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Fetch from Supabase, then import any legacy localStorage reports (once).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchFromSupabase();
      if (!cancelled) migrateLocalCustomReports(currentWorkspaceId);
    })();
    return () => { cancelled = true; };
  }, [fetchFromSupabase, currentWorkspaceId]);

  // Item-entity reports scoped to the current workspace (matches the item-list
  // Saved Reports panel — same store, so reports appear in both places).
  const reports = useMemo(
    () =>
      allReports.filter(
        (r) =>
          r.entityType === 'items' &&
          (!r.workspaceId || r.workspaceId === currentWorkspaceId)
      ),
    [allReports, currentWorkspaceId]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return reports;
    const q = query.toLowerCase();
    return reports.filter(
      (r) => r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
    );
  }, [reports, query]);

  const handleDelete = (id: string) => {
    const report = reports.find((r) => r.id === id);
    if (!report) return;
    if (!window.confirm(`Delete "${report.name}"? This cannot be undone.`)) return;
    const { error } = deleteReport(id);
    if (error) {
      addNotification({ type: 'error', title: 'Report not deleted', message: error, duration: 6000 });
      return;
    }
    addNotification({ type: 'success', title: 'Report deleted', message: `"${report.name}" removed`, duration: 3000 });
  };

  const handleRename = (id: string, name: string) => {
    if (!name) return;
    updateReport(id, { name });
    setRenamingId(null);
  };

  // Group by month (most recent first)
  const grouped = useMemo(() => {
    const map = new Map<string, typeof reports>();
    const sorted = [...filtered].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    for (const r of sorted) {
      const label = new Date(r.updatedAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      const arr = map.get(label) ?? [];
      arr.push(r);
      map.set(label, arr);
    }
    return map;
  }, [filtered]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<BarChart3 className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Custom Reports"
        subtitle="Reports saved for this workspace, and any you kept to yourself"
        actions={
          <Link
            to="/prioritization/reports/custom/new"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Report
          </Link>
        }
      />

      {reports.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-dashed border-[var(--rule)] p-14 text-center">
          <BookOpen className="h-9 w-9 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-semibold text-[var(--text-sec)]">No custom reports yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-1 mb-5">
            Build a report using the designer — filter items, configure views, then save.
          </p>
          <button
            onClick={() => navigate('/prioritization/reports/custom/new')}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Open Report Designer
          </button>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reports…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-[var(--bg-raised)]"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] py-8 text-center">No reports match "{query}"</p>
          ) : (
            <div className="space-y-6">
              {Array.from(grouped.entries()).map(([month, monthReports]) => (
                <div key={month}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">{month}</span>
                  </div>
                  <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
                    {monthReports.map((report) => (
                      <div
                        key={report.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)]/60 transition-colors group"
                      >
                        <div className="w-8 h-8 bg-[var(--hover-tint)] flex items-center justify-center flex-shrink-0">
                          <Folder className="h-4 w-4 text-[var(--text-ter)]" />
                        </div>

                        <div className="flex-1 min-w-0">
                          {renamingId === report.id ? (
                            <InlineRename
                              value={report.name}
                              onSave={(v) => handleRename(report.id, v)}
                              onCancel={() => setRenamingId(null)}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/prioritization/reports/custom/${report.id}`}
                                className="min-w-0 text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 truncate block transition-colors"
                              >
                                {report.name}
                              </Link>
                              {report.visibility === 'private' && <JustMeTag />}
                            </div>
                          )}
                          {report.description && renamingId !== report.id && (
                            <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{report.description}</p>
                          )}
                        </div>

                        <span className="text-xs text-[var(--text-ter)] flex-shrink-0 hidden sm:block">
                          {new Date(report.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>

                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setRenamingId(report.id)}
                            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                            title="Rename"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(report.id)}
                            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
